import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, CircularProgress, 
  TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, FormControl, InputLabel, Select, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, IconButton, Chip, Tooltip
} from '@mui/material';
// Fix icon import paths
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { bookOfTheDayAPI, booksAPI } from '../services/api';
import { useAudioPlayer } from '../context/AudioPlayerContext';

export default function BookOfTheDayManagement() {
  const [entries, setEntries] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    bookId: '',
    description: '',
    bannerUrl: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [todaysFeatured, setTodaysFeatured] = useState(null);
  
  const { playTrack } = useAudioPlayer();
  const navigate = useNavigate(); // Add navigation hook

  useEffect(() => {
    fetchEntries();
    fetchBooks();
    fetchToday();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await bookOfTheDayAPI.getAllEntries();
      setEntries(response.data.data || []);
    } catch (error) {
      console.error('Error fetching book of the day entries:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch book of the day entries',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await booksAPI.getAllBooks(1, 100);
      setBooks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchToday = async () => {
    try {
      const response = await bookOfTheDayAPI.getFeaturedForToday();
      setTodaysFeatured(response.data);
    } catch (error) {
      console.error('Error fetching today\'s featured book:', error);
      // Don't show an error message as this might be intentional (no book for today)
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setIsEditing(true);
      setCurrentEntry(entry);
      setFormData({
        date: entry.date.split('T')[0], // Extract just the date part
        bookId: entry.book.id,
        description: entry.description || '',
        bannerUrl: entry.bannerUrl || '',
      });
    } else {
      setIsEditing(false);
      setCurrentEntry(null);
      setFormData({
        date: format(selectedDate, 'yyyy-MM-dd'),
        bookId: '',
        description: '',
        bannerUrl: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setCurrentEntry(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing && currentEntry) {
        await bookOfTheDayAPI.updateEntry(currentEntry.id, {
          bookId: formData.bookId,
          description: formData.description,
          bannerUrl: formData.bannerUrl,
        });
        setSnackbar({
          open: true,
          message: 'Book of the Day entry updated successfully',
          severity: 'success',
        });
      } else {
        await bookOfTheDayAPI.createEntry({
          date: formData.date,
          bookId: formData.bookId,
          description: formData.description,
          bannerUrl: formData.bannerUrl,
        });
        setSnackbar({
          open: true,
          message: 'Book of the Day entry created successfully',
          severity: 'success',
        });
      }
      
      // Refresh data
      fetchEntries();
      fetchToday();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving Book of the Day entry:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to save Book of the Day entry',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      setLoading(true);
      await bookOfTheDayAPI.deleteEntry(entryToDelete.id);
      
      setSnackbar({
        open: true,
        message: 'Book of the Day entry deleted successfully',
        severity: 'success',
      });
      
      fetchEntries();
      fetchToday();
    } catch (error) {
      console.error('Error deleting Book of the Day entry:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete Book of the Day entry',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
      setEntryToDelete(null);
    }
  };

  const handlePlayAudio = (book) => {
    if (!book || !book.audioUrls) return;
    
    // Get first available audio
    const duration = Object.keys(book.audioUrls)[0];
    const audioUrl = book.audioUrls[duration];
    
    if (audioUrl) {
      playTrack({
        url: audioUrl,
        title: `${book.title} - ${duration} minutes`,
        artist: book.author
      });
    }
  };

  // Add a navigation handler to go to book details
  const handleBookClick = (bookId) => {
    if (!bookId) return;
    navigate(`/books/${bookId}`);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Book of the Day Management
      </Typography>

      {/* Today's Featured Book */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon color="primary" />
          Today's Featured Book
        </Typography>

        {todaysFeatured ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box 
                sx={{ 
                  width: '100%', 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              >
                <img 
                  src={todaysFeatured.cover} 
                  alt={todaysFeatured.title}
                  style={{ width: '100%', display: 'block' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/200x300?text=No+Cover';
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography variant="h6" sx={{ mb: 1 }}>{todaysFeatured.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{todaysFeatured.author}</Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Featured Description:</strong><br />
                {todaysFeatured.featuredDescription || 'No featured description available.'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {todaysFeatured.durationOptions?.map((duration) => (
                  <Chip 
                    key={duration} 
                    label={`${duration} min`} 
                    color="primary" 
                    variant="outlined"
                    onClick={() => handlePlayAudio(todaysFeatured)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>

              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  const entry = entries.find(e => 
                    e.book.id === todaysFeatured.id && 
                    e.date.startsWith(format(new Date(), 'yyyy-MM-dd'))
                  );
                  if (entry) {
                    handleOpenDialog(entry);
                  }
                }}
                startIcon={<EditIcon />}
              >
                Edit Today's Featured
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info">
            No book is featured for today. Use the calendar to set a featured book for today.
          </Alert>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Calendar and Form */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Schedule Featured Books</Typography>
            {/* Replace the DatePicker with a simple TextField */}
            <TextField
              label="Select Date"
              type="date"
              value={formData.date}
              onChange={(e) => {
                const newDate = e.target.value;
                setSelectedDate(new Date(newDate));
                setFormData({
                  ...formData,
                  date: newDate
                });
              }}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              fullWidth
            >
              Set Featured Book
            </Button>
          </Paper>
        </Grid>

        {/* List of Entries */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Scheduled Books</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
              </Box>
            ) : entries.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Book</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {new Date(entry.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              component="img"
                              src={entry.book.cover}
                              alt={entry.book.title}
                              sx={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 1 }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/40x60?text=No+Cover';
                              }}
                            />
                            <Typography
                              variant="body1"
                              sx={{ 
                                cursor: 'pointer', 
                                color: 'primary.main',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => handleBookClick(entry.book?.id)}
                            >
                              {entry.book?.title || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {entry.book?.author || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {entry.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenDialog(entry)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDeleteClick(entry)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No Book of the Day entries found. Use the form to create one.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Book of the Day' : 'Add Book of the Day'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="book-select-label">Book</InputLabel>
                <Select
                  labelId="book-select-label"
                  name="bookId"
                  value={formData.bookId}
                  onChange={handleInputChange}
                  label="Book"
                  required
                >
                  {books.map((book) => (
                    <MenuItem key={book.id} value={book.id}>
                      {book.title} - {book.author}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Featured Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Banner URL (optional)"
                name="bannerUrl"
                value={formData.bannerUrl}
                onChange={handleInputChange}
                fullWidth
                helperText="Leave empty to use the book cover"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.bookId}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this Book of the Day entry?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
